import { AthleteAttendanceTeam } from '../../../../models/attendance.models';
import { SportBreakdownEntry, StatsResponse, WeekBreakdownItem } from '../../../../models/stats.models';
import {
  formatAttendancePercent,
  getTeamLineColor,
  getTeamPeriodAttendance,
  getTeamWeekAttendance,
  hasPeriodAttendanceData,
  hasSeasonAttendanceData,
  hasWeekAttendanceData,
} from '../../../../components/team-attendance-breakdown/team-attendance-breakdown.helpers';
import {
  formatApiDateDisplay,
  formatPeriodDateRangeLabel,
  formatZoneDistanceCell,
  formatZoneDurationCell,
  getPeriodDateRange,
  groupWeeksByPeriod,
  isCurrentWeek,
} from '../../../../components/period-summary/period-summary.helpers';
import { getSportKeysFromPeriodBreakdown } from '../../../../components/sport-breakdown/sport-breakdown.helpers';
import { getSportTypeKeys } from '../../../../components/sport-type-breakdown/sport-type-breakdown.helpers';
import { getMeasurementUnit, ZONE_CONFIGS } from '../../../../helpers/general.helpers';
import { SPORT_LAYER_COLORS } from '../../training-log.constants';
import { getDurationDistanceMetric, getSportLayerColor, MetricMode, safeZoneArray } from '../../training-log.helpers';
import { formatEffortShort, getRpeDotColor } from '../log/log.helpers';

export type RawDataRowType = 'week' | 'period' | 'season';

export interface RawDataColumn {
  id: string;
  colIndex: number;
  groupLabel: string;
  subLabel: string;
  color?: string;
  separator?: boolean;
  cellClassName?: string;
}

export type RawDataColumnGroupLabelTemplate =
  | 'none'
  | 'fixed'
  | 'volume'
  | 'zones'
  | 'sport'
  | 'sportTypes'
  | 'attendance';

export interface RawDataColumnBase {
  id: string;
  subLabel: string;
  color?: string;
  separator?: boolean;
  cellClassName?: string;
  groupLabelTemplate: RawDataColumnGroupLabelTemplate;
  fixedGroupLabel?: string;
  sportKey?: string;
  showGroupLabel: boolean;
}

export interface RawDataCellValue {
  text: string;
  color?: string;
  fontWeight?: number;
  className?: string;
}

export interface RawDataRow {
  type: RawDataRowType;
  classNames: string[];
  periodIndex?: number;
  weekIndex?: number;
  weekLabel: string;
  dateLabel: string;
  navigable: boolean;
  cells: Record<string, RawDataCellValue>;
}

export interface RawDataTableModel {
  columns: RawDataColumn[];
  rows: RawDataRow[];
}

type MetricColumnSpec =
  | { kind: 'volume'; id: string; cellClassName?: string }
  | { kind: 'zone'; id: string; zoneIndex: number; cellClassName?: string }
  | { kind: 'sport'; id: string; sportKey: string; cellClassName?: string }
  | { kind: 'sport-type'; id: string; sportKey: string; typeKey: string; cellClassName?: string };

const formatMetricCell = (rawValue: number, mode: MetricMode, emptyWhenZero = true): string => {
  if (emptyWhenZero && rawValue === 0) {
    return '—';
  }
  if (mode === 'duration') {
    return formatZoneDurationCell(rawValue);
  }
  return formatZoneDistanceCell(rawValue);
};

const getSportColor = (sportKey: string, index: number): string =>
  SPORT_LAYER_COLORS[sportKey] ?? getSportLayerColor(sportKey, index);

const getUnitSuffix = (mode: MetricMode): string => (mode === 'duration' ? 'hrs' : getMeasurementUnit());

const resolveGroupLabel = (base: RawDataColumnBase, mode: MetricMode): string => {
  if (!base.showGroupLabel) {
    return '';
  }

  switch (base.groupLabelTemplate) {
    case 'none':
      return '';
    case 'fixed':
      return base.fixedGroupLabel ?? '';
    case 'volume':
      return `Volume (${getUnitSuffix(mode)})`;
    case 'zones':
      return `Zones (${getUnitSuffix(mode)})`;
    case 'sport':
      return `Sport (${getUnitSuffix(mode)})`;
    case 'sportTypes':
      return `${base.sportKey ?? ''} Types (${getUnitSuffix(mode)})`;
    case 'attendance':
      return 'Team Attendance';
    default:
      return '';
  }
};

const getEffortCell = (average: number | undefined, blank = false): RawDataCellValue => {
  if (blank || average === undefined || !Number.isFinite(average) || average <= 0) {
    return { text: '—' };
  }
  return {
    text: formatEffortShort(average),
    color: getRpeDotColor(average),
    fontWeight: 600,
  };
};

const buildTeamByIdMap = (teams: AthleteAttendanceTeam[]): Map<number, AthleteAttendanceTeam> =>
  new Map(teams.map((team) => [team.teamId, team]));

const getAttendanceCell = (
  teamById: Map<number, AthleteAttendanceTeam>,
  teamId: number,
  getPercent: (team: AthleteAttendanceTeam) => { practicePercent: number; hasData: boolean }
): RawDataCellValue => {
  const team = teamById.get(teamId);
  if (!team) {
    return { text: '—', className: 'rt-att' };
  }
  const { practicePercent, hasData } = getPercent(team);
  if (!hasData) {
    return { text: '—', className: 'rt-att' };
  }
  return {
    text: formatAttendancePercent(practicePercent),
    className: 'rt-att',
  };
};

const buildSportTypeGroups = (
  sportKeys: string[],
  stats: StatsResponse
): { sportKey: string; typeKeys: string[] }[] => {
  const periods = stats.periodBreakdown ?? [];
  const sportBreakdown = stats.sportBreakdown ?? {};

  return sportKeys
    .map((sportKey) => ({
      sportKey,
      typeKeys: getSportTypeKeys(sportKey, periods, sportBreakdown),
    }))
    .filter(({ typeKeys }) => typeKeys.length > 0);
};

export const buildRawDataColumnBase = (
  stats: StatsResponse | null,
  attendanceTeams: AthleteAttendanceTeam[] | null | undefined
): RawDataColumnBase[] => {
  const columns: RawDataColumnBase[] = [];

  const addColumn = (column: RawDataColumnBase) => {
    columns.push(column);
  };

  addColumn({
    id: 'week',
    subLabel: '',
    cellClassName: 'rt-wk',
    groupLabelTemplate: 'none',
    showGroupLabel: false,
  });

  addColumn({
    id: 'date',
    subLabel: '',
    cellClassName: 'rt-dt',
    groupLabelTemplate: 'none',
    showGroupLabel: false,
  });

  addColumn({
    id: 'volume',
    subLabel: 'Actual',
    separator: true,
    cellClassName: 'rt-actual',
    groupLabelTemplate: 'volume',
    showGroupLabel: true,
  });

  addColumn({
    id: 'effort',
    subLabel: 'Avg Effort',
    separator: true,
    groupLabelTemplate: 'fixed',
    fixedGroupLabel: 'Effort',
    showGroupLabel: true,
  });

  ZONE_CONFIGS.forEach((zone, zi) => {
    addColumn({
      id: `zone-${zi}`,
      subLabel: zone.label,
      color: zone.textColor,
      separator: zi === 0,
      cellClassName: `rt-z rt-z${zi}`,
      groupLabelTemplate: 'zones',
      showGroupLabel: zi === 0,
    });
  });

  const sportKeys = stats
    ? getSportKeysFromPeriodBreakdown(stats.periodBreakdown ?? [], stats.sportBreakdown ?? {})
    : [];

  sportKeys.forEach((sportKey, index) => {
    addColumn({
      id: `sport::${sportKey}`,
      subLabel: sportKey,
      color: getSportColor(sportKey, index),
      separator: index === 0,
      cellClassName: 'rt-sport',
      groupLabelTemplate: 'sport',
      showGroupLabel: index === 0,
    });
  });

  if (stats && sportKeys.length > 0) {
    buildSportTypeGroups(sportKeys, stats).forEach(({ sportKey, typeKeys }) => {
      typeKeys.forEach((typeKey, typeIndex) => {
        addColumn({
          id: `sport-type-${sportKey}::${typeKey}`,
          subLabel: typeKey,
          separator: typeIndex === 0,
          cellClassName: 'rt-sport',
          groupLabelTemplate: 'sportTypes',
          sportKey,
          showGroupLabel: typeIndex === 0,
        });
      });
    });
  }

  const teams = attendanceTeams ?? [];
  teams.forEach((team, index) => {
    addColumn({
      id: `att-${team.teamId}`,
      subLabel: team.teamName,
      color: getTeamLineColor(team, index),
      separator: index === 0,
      cellClassName: 'rt-att',
      groupLabelTemplate: 'attendance',
      showGroupLabel: index === 0,
    });
  });

  return columns;
};

export const hydrateRawDataColumns = (base: RawDataColumnBase[], mode: MetricMode): RawDataColumn[] =>
  base.map((column, colIndex) => ({
    id: column.id,
    colIndex,
    groupLabel: resolveGroupLabel(column, mode),
    subLabel: column.subLabel,
    color: column.color,
    separator: column.separator,
    cellClassName: column.cellClassName,
  }));

export const buildRawDataColumns = (
  stats: StatsResponse | null,
  attendanceTeams: AthleteAttendanceTeam[] | null | undefined,
  mode: MetricMode
): RawDataColumn[] => hydrateRawDataColumns(buildRawDataColumnBase(stats, attendanceTeams), mode);

const buildMetricColumnSpecs = (columns: RawDataColumn[]): MetricColumnSpec[] => {
  const specs: MetricColumnSpec[] = [];

  columns.forEach((col) => {
    if (col.id === 'volume') {
      specs.push({ kind: 'volume', id: col.id, cellClassName: col.cellClassName });
      return;
    }

    if (col.id.startsWith('zone-')) {
      const zoneIndex = parseInt(col.id.replace('zone-', ''), 10);
      specs.push({ kind: 'zone', id: col.id, zoneIndex, cellClassName: col.cellClassName });
      return;
    }

    if (col.id.startsWith('sport-type-')) {
      const payload = col.id.slice('sport-type-'.length);
      const sep = payload.indexOf('::');
      const sportKey = sep >= 0 ? payload.slice(0, sep) : '';
      const typeKey = sep >= 0 ? payload.slice(sep + 2) : '';
      specs.push({ kind: 'sport-type', id: col.id, sportKey, typeKey, cellClassName: col.cellClassName });
      return;
    }

    if (col.id.startsWith('sport::')) {
      const sportKey = col.id.slice('sport::'.length);
      specs.push({ kind: 'sport', id: col.id, sportKey, cellClassName: col.cellClassName });
    }
  });

  return specs;
};

const buildMetricCells = (
  metricSpecs: MetricColumnSpec[],
  mode: MetricMode,
  durationSeconds: number,
  distance: number,
  durationBreakdown: number[] | undefined,
  distanceBreakdown: number[] | undefined,
  sport: Record<string, SportBreakdownEntry> | undefined,
  emptyWhenZero = true
): Record<string, RawDataCellValue> => {
  const cells: Record<string, RawDataCellValue> = {};
  const volume = mode === 'duration' ? durationSeconds : distance;
  const breakdown = mode === 'duration' ? durationBreakdown : distanceBreakdown;
  const safeZones = safeZoneArray(breakdown);

  metricSpecs.forEach((spec) => {
    if (spec.kind === 'volume') {
      cells[spec.id] = {
        text: formatMetricCell(volume, mode, emptyWhenZero),
        fontWeight: 600,
        className: spec.cellClassName,
      };
      return;
    }

    if (spec.kind === 'zone') {
      cells[spec.id] = {
        text: formatMetricCell(safeZones[spec.zoneIndex] ?? 0, mode, emptyWhenZero),
        className: spec.cellClassName,
      };
      return;
    }

    if (spec.kind === 'sport-type') {
      const rawValue = getDurationDistanceMetric(sport?.[spec.sportKey]?.sportType?.[spec.typeKey], mode);
      cells[spec.id] = {
        text: formatMetricCell(rawValue, mode, emptyWhenZero),
        className: spec.cellClassName,
      };
      return;
    }

    if (spec.kind === 'sport') {
      const rawValue = getDurationDistanceMetric(sport?.[spec.sportKey], mode);
      cells[spec.id] = {
        text: formatMetricCell(rawValue, mode, emptyWhenZero),
        className: spec.cellClassName,
      };
    }
  });

  return cells;
};

const buildAttendanceCells = (
  columns: RawDataColumn[],
  teamById: Map<number, AthleteAttendanceTeam>,
  getPercent: (team: AthleteAttendanceTeam) => { practicePercent: number; hasData: boolean }
): Record<string, RawDataCellValue> => {
  const cells: Record<string, RawDataCellValue> = {};

  columns
    .filter((col) => col.id.startsWith('att-'))
    .forEach((col) => {
      const teamId = parseInt(col.id.replace('att-', ''), 10);
      cells[col.id] = getAttendanceCell(teamById, teamId, getPercent);
    });

  return cells;
};

const isEmptyWeek = (week: WeekBreakdownItem): boolean =>
  (week.duration?.total ?? 0) === 0 && (week.distance?.total ?? 0) === 0 && (week.trainings?.total ?? 0) === 0;

export const buildRawDataRows = (
  stats: StatsResponse | null,
  attendanceTeams: AthleteAttendanceTeam[] | null | undefined,
  columns: RawDataColumn[],
  mode: MetricMode
): RawDataRow[] => {
  if (!stats) {
    return [];
  }

  const teams = attendanceTeams ?? [];
  const teamById = buildTeamByIdMap(teams);
  const metricSpecs = buildMetricColumnSpecs(columns);
  const periods = stats.periodBreakdown ?? [];
  const weeks = stats.weekBreakdown ?? [];
  const rows: RawDataRow[] = [];

  periods.forEach((period) => {
    const periodWeeks = groupWeeksByPeriod(weeks, period.index);

    periodWeeks.forEach((week) => {
      const empty = isEmptyWeek(week);
      const metricCells = buildMetricCells(
        metricSpecs,
        mode,
        week.duration?.total ?? 0,
        week.distance?.total ?? 0,
        week.totalDurationBreakdown,
        week.totalDistanceBreakdown,
        week.sport,
        empty
      );

      const attendanceCells = buildAttendanceCells(columns, teamById, (team) => {
        const entry = getTeamWeekAttendance(team, week.index);
        return {
          practicePercent: entry?.practicePercent ?? 0,
          hasData: hasWeekAttendanceData(entry),
        };
      });

      rows.push({
        type: 'week',
        classNames: [empty ? 'rt-empty' : '', isCurrentWeek(week.week) ? 'rt-current' : ''].filter(Boolean),
        periodIndex: period.index,
        weekIndex: week.index,
        weekLabel: `Week ${week.index}`,
        dateLabel: formatApiDateDisplay(week.week, 'MMM D'),
        navigable: true,
        cells: {
          week: { text: `Week ${week.index}`, className: 'rt-wk' },
          date: { text: formatApiDateDisplay(week.week, 'MMM D'), className: 'rt-dt' },
          effort: getEffortCell(week.effortRatings?.average, empty),
          ...metricCells,
          ...attendanceCells,
        },
      });
    });

    const { start, end } = getPeriodDateRange(period, weeks);
    const periodDateLabel =
      start && end ? `${start.format('MMM D')} – ${end.format('MMM D')}` : formatPeriodDateRangeLabel(period.period);

    const periodMetricCells = buildMetricCells(
      metricSpecs,
      mode,
      period.duration?.total ?? 0,
      period.distance?.total ?? 0,
      period.totalDurationBreakdown,
      period.totalDistanceBreakdown,
      period.sport,
      false
    );

    const periodAttendanceCells = buildAttendanceCells(columns, teamById, (team) => {
      const entry = getTeamPeriodAttendance(team, period.index);
      return {
        practicePercent: entry?.practicePercent ?? 0,
        hasData: hasPeriodAttendanceData(entry),
      };
    });

    rows.push({
      type: 'period',
      classNames: ['rt-period-row'],
      periodIndex: period.index,
      weekLabel: `Period ${period.index}`,
      dateLabel: periodDateLabel,
      navigable: false,
      cells: {
        week: { text: `Period ${period.index}`, className: 'rt-wk' },
        date: { text: periodDateLabel, className: 'rt-dt' },
        effort: getEffortCell(period.effortRatings?.average),
        ...periodMetricCells,
        ...periodAttendanceCells,
      },
    });
  });

  const seasonMetricCells = buildMetricCells(
    metricSpecs,
    mode,
    stats.totalDuration ?? 0,
    stats.totalDistance ?? 0,
    stats.totalDurationBreakdown,
    stats.totalDistanceBreakdown,
    stats.sportBreakdown,
    false
  );

  const seasonAttendanceCells = buildAttendanceCells(columns, teamById, (team) => ({
    practicePercent: team.practicePercent ?? 0,
    hasData: hasSeasonAttendanceData(team),
  }));

  rows.push({
    type: 'season',
    classNames: ['rt-total-row'],
    weekLabel: 'Season Total',
    dateLabel: `${weeks.length} weeks`,
    navigable: false,
    cells: {
      week: { text: 'Season Total', className: 'rt-wk' },
      date: { text: `${weeks.length} weeks`, className: 'rt-dt' },
      effort: getEffortCell(undefined, true),
      ...seasonMetricCells,
      ...seasonAttendanceCells,
    },
  });

  return rows;
};

export const buildRawDataTable = (
  stats: StatsResponse | null,
  attendanceTeams: AthleteAttendanceTeam[] | null | undefined,
  mode: MetricMode
): RawDataTableModel => {
  const columns = buildRawDataColumns(stats, attendanceTeams, mode);
  const rows = buildRawDataRows(stats, attendanceTeams, columns, mode);

  return { columns, rows };
};
